/**
 * Filters drawer.
 *
 * The drawer is already a working filter form without any of this: it is a
 * `<form method="get">` inside a `<dialog>`, so "Apply filters" navigates to a
 * real filtered URL. This element adds three things:
 *
 *   1. opening from any `[data-facets-open]` trigger on the page
 *   2. closing on the backdrop, on the close button, and unlocking page scroll
 *      (showModal() makes the background inert but does not stop it scrolling)
 *   3. keeping the count badge honest while the drawer is open, so the number
 *      beside "FILTERS" tracks what the shopper has ticked rather than what the
 *      last page load carried
 *   4. with `data-auto-apply`, applying each change as it is made. The drawer
 *      only announces the filtered URL as a cancelable `facets-drawer:apply`;
 *      the consuming section renders it and calls refresh() with the fresh
 *      drawer. Nobody listening means the form submits, so a tick is never
 *      silently dropped. The Apply button is hidden here rather than left out
 *      of the markup, so the form still submits with JavaScript off.
 *   5. with `data-apply-param`, applying a change to that one facet the same
 *      way while every other group waits for the Apply button. The drawer
 *      sets it to the category facet, because the size group is drawn from
 *      the category that is applied (component-facets-sizes.liquid) and a
 *      category that waited for Apply would leave the sizes locked behind a
 *      button. The URL it announces carries whatever else is ticked, so a
 *      colour ticked just before the category is applied with it.
 *   6. through refresh(), keeping what the shopper has done to the drawer
 *      while its groups are swapped for freshly rendered ones: the scroll
 *      position, the focused control, and which groups they have opened or
 *      closed. The groups start closed (component-facets-drawer.liquid), so
 *      without the last of those a category ticked in one group would fold
 *      every group around it.
 *
 *   7. keeping the Brand group honest. It is drawn from a fixed list of tags
 *      (component-brand-values.liquid), and Shopify neither counts nor reports
 *      as applied a tag outside the 100 its facet returns — so the server
 *      cannot tick an applied brand, nor tell which brands the collection
 *      carries. Both are answered here: the boxes are ticked from the URL, and
 *      on first open each brand is counted against the result set (see
 *      loadBrandCounts) and a brand with no products is hidden.
 *
 * Escape, the focus trap and returning focus to the trigger are the browser's,
 * from showModal() — the same choice component-cart-drawer.js made.
 */

const BRAND_PARAM = 'filter.p.tag';
const BRAND_COUNT_SECTION = 'facets-brand-count';
const BRAND_COUNT_CONCURRENCY = 4;
const BRAND_COUNT_STORAGE = 'facets-drawer:brand-counts:';

/** Counts already known or on their way, keyed on the unfiltered results URL. */
const brandCounts = new Map();

/**
 * The results URL a brand is counted against: the collection or search the
 * shopper is on, with every filter, the sort and the page taken off. Presence
 * is a question about the collection, not about whatever else is ticked, and
 * one answer per collection is what lets it be cached. A search keeps `q`.
 *
 * `basePath` is the path the drawer's form posts to, when it is not the
 * page's own: on a menu brand page (/collections/akron/new_era) the brand is
 * taken out of the path, and counting against the page's path would AND every
 * other brand with New Era and find none.
 *
 * @param {URL} url
 * @param {string} [basePath]
 * @returns {URL}
 */
function brandBase(url, basePath) {
  const base = new URL(basePath || url.pathname, window.location.origin);
  for (const [name, value] of url.searchParams) {
    if (name.startsWith('filter.') || ['page', 'sort_by', 'section_id'].includes(name)) continue;
    base.searchParams.append(name, value);
  }
  return base;
}

/**
 * How many products `base` holds with one brand tag applied, or null when the
 * request fails — an unknown brand is shown, never hidden.
 *
 * @param {URL} base
 * @param {string} tag
 * @returns {Promise<number | null>}
 */
async function countBrand(base, tag) {
  const request = new URL(base);
  request.searchParams.set(BRAND_PARAM, tag);
  request.searchParams.set('section_id', BRAND_COUNT_SECTION);

  try {
    const response = await fetch(request);
    if (!response.ok) throw new Error(`Brand count failed: ${response.status}`);

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const count = Number.parseInt(html.querySelector('[data-brand-count]')?.textContent ?? '', 10);
    return Number.isNaN(count) ? null : count;
  } catch (error) {
    console.error(error);
    return null;
  }
}

/**
 * Product counts for `tags` on `base`, as `{ tag: count | null }`.
 *
 * One request per brand, because Shopify will count a tag only when it is the
 * filter applied. A few at a time rather than all at once, and remembered for
 * the browser session, so a collection is counted once however often the
 * drawer is opened, re-rendered or the page reloaded. A run with a failed
 * count is neither stored nor kept for the page, so the next open asks again.
 *
 * @param {URL} base
 * @param {string[]} tags
 * @returns {Promise<Record<string, number | null>>}
 */
function loadBrandCounts(base, tags) {
  const key = base.href;
  if (brandCounts.has(key)) return brandCounts.get(key);

  const load = (async () => {
    const stored = storedBrandCounts(base, tags);
    if (stored) return stored;

    const counts = {};
    const queue = [...tags];
    const worker = async () => {
      while (queue.length) {
        const tag = queue.shift();
        counts[tag] = await countBrand(base, tag);
      }
    };
    await Promise.all(Array.from({ length: BRAND_COUNT_CONCURRENCY }, worker));

    if (Object.values(counts).every((count) => count !== null)) {
      try {
        sessionStorage.setItem(BRAND_COUNT_STORAGE + key, JSON.stringify(counts));
      } catch {
        /* Not remembered across loads; the in-page copy still holds. */
      }
    } else {
      brandCounts.delete(key);
    }

    return counts;
  })();

  brandCounts.set(key, load);
  return load;
}

/**
 * The counts stored this session for `base`, or null unless every one of
 * `tags` is in them — a brand added to the list since is counted, not
 * guessed at.
 *
 * @param {URL} base
 * @param {string[]} tags
 * @returns {Record<string, number | null> | null}
 */
function storedBrandCounts(base, tags) {
  try {
    const stored = JSON.parse(sessionStorage.getItem(BRAND_COUNT_STORAGE + base.href) ?? 'null');
    if (stored && tags.every((tag) => tag in stored)) return stored;
  } catch {
    /* Storage can be unavailable or hold junk; counting again is the fallback. */
  }
  return null;
}

if (!customElements.get('facets-drawer')) {
  customElements.define(
    'facets-drawer',
    class FacetsDrawer extends HTMLElement {
      static SCROLL_LOCK_CLASS = 'facets-drawer-open';

      constructor() {
        super();
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onInnerClick = this.onInnerClick.bind(this);
        this.onInput = this.onInput.bind(this);
        this.onChange = this.onChange.bind(this);
        this.unlock = this.unlock.bind(this);
      }

      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        this.form = this.querySelector('form');
        this.badge = this.querySelector('[data-facets-count]');

        if (this.autoApply) {
          const footer = this.querySelector('.facets-drawer__footer');
          if (footer) footer.hidden = true;
        }

        document.addEventListener('click', this.onDocumentClick);
        this.addEventListener('click', this.onInnerClick);
        this.form?.addEventListener('change', this.onChange);
        this.form?.addEventListener('change', this.onInput);
        this.form?.addEventListener('input', this.onInput);
        /* `cancel` fires on Escape before `close`; listening to both means the
           page cannot be left scroll-locked if either is missed. */
        this.dialog?.addEventListener('close', this.unlock);
        this.dialog?.addEventListener('cancel', this.unlock);

        this.fillPriceFallback(new URL(window.location.href));

        /* A drawer swapped in by a grid render connects before that render's
           URL is pushed, so the grid hands it the URL it was rendered for.
           Read once and dropped: by any later connect that URL is history. */
        const renderedUrl = this.dataset.renderedUrl ?? window.location.href;
        delete this.dataset.renderedUrl;
        this.syncBrands(new URL(renderedUrl, window.location.origin), true);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        this.removeEventListener('click', this.onInnerClick);
        this.form?.removeEventListener('change', this.onChange);
        this.form?.removeEventListener('change', this.onInput);
        this.form?.removeEventListener('input', this.onInput);
        this.dialog?.removeEventListener('close', this.unlock);
        this.dialog?.removeEventListener('cancel', this.unlock);
        document.documentElement.classList.remove(FacetsDrawer.SCROLL_LOCK_CLASS);
      }

      onDocumentClick(event) {
        if (!event.target.closest('[data-facets-open]')) return;
        event.preventDefault();
        this.open();
      }

      onInnerClick(event) {
        /* With showModal(), a click on the backdrop targets the dialog itself. */
        if (event.target === this.dialog || event.target.closest('[data-facets-close]')) {
          this.close();
        }
      }

      open() {
        if (!this.dialog || this.dialog.open) return;
        /* Lock first: showModal() scrolls the newly focused control into view,
           and with the page still scrollable that reads as a jump behind the
           drawer. */
        document.documentElement.classList.add(FacetsDrawer.SCROLL_LOCK_CLASS);
        this.dialog.showModal();
        this.showBrandCounts();
      }

      close() {
        this.dialog?.close();
        /* Also here, not only on the `close` event: Escape and other
           browser-initiated closes never call this method, and a page left
           scroll-locked is the worse failure to risk. The unlock is idempotent. */
        this.unlock();
      }

      unlock() {
        document.documentElement.classList.remove(FacetsDrawer.SCROLL_LOCK_CLASS);
      }

      get autoApply() {
        return this.hasAttribute('data-auto-apply');
      }

      get applyParam() {
        return this.dataset.applyParam ?? '';
      }

      onInput() {
        this.updateCount();
      }

      onChange(event) {
        if (this.autoApply || (this.applyParam && event.target.name === this.applyParam)) this.apply();
      }

      apply() {
        if (!this.form) return;

        const url = new URL(this.form.action, window.location.origin);
        /* An untouched price field submits as `filter.v.price.gte=`, which
           Shopify ignores but which would still land in the address bar and
           the history entry. A plain submit sends it too; this URL is the one
           that is pushed, so it is tidied here. */
        const fields = Array.from(new FormData(this.form)).filter(([, value]) => value !== '');
        url.search = new URLSearchParams(fields).toString();

        const event = new CustomEvent('facets-drawer:apply', { bubbles: true, cancelable: true, detail: { url } });
        const handled = !this.dispatchEvent(event);
        if (!handled) this.form.requestSubmit();
      }

      /**
       * Swap in the groups of a freshly rendered drawer while this one stays
       * open, keeping the shopper's scroll position, focused control, and
       * open and closed groups. The form's action and its hidden inputs come
       * across too: a tag pill changes the action, and the sort rides in a
       * hidden input, so a stale copy of either would make the next Apply
       * land on the wrong view.
       */
      refresh(freshDrawer, url) {
        const groups = this.querySelector('.facets-drawer__groups');
        const freshGroups = freshDrawer.querySelector('.facets-drawer__groups');
        if (!groups || !freshGroups) return;

        const freshForm = freshDrawer.querySelector('form');
        if (this.form && freshForm) {
          this.form.setAttribute('action', freshForm.getAttribute('action') ?? '');
          for (const input of this.form.querySelectorAll(':scope > input[type="hidden"]')) input.remove();
          this.form.prepend(...freshForm.querySelectorAll(':scope > input[type="hidden"]'));
        }

        const focused = groups.contains(document.activeElement) ? document.activeElement : null;
        const scrollTop = groups.scrollTop;
        const priceMax = groups.querySelector('.facets-drawer__price-input[max]')?.getAttribute('max');

        /* Each group's state as the shopper left it, keyed on its facet. The
           fresh markup draws every group the server's way — closed, unless
           applied — which would undo their toggles; a group new to this render
           has no entry and keeps the server's choice. This is also what shows
           the sizes the moment a category is ticked: the locked size group
           was open on its hint, so the unlocked one is put back open. */
        const toggled = new Map();
        for (const group of groups.querySelectorAll('details[data-facets-group]')) {
          toggled.set(group.dataset.facetsGroup, group.open);
        }

        groups.replaceChildren(...freshGroups.childNodes);
        groups.scrollTop = scrollTop;

        for (const group of groups.querySelectorAll('details[data-facets-group]')) {
          const open = toggled.get(group.dataset.facetsGroup);
          if (open !== undefined) group.open = open;
        }

        if (focused?.name) {
          for (const control of groups.querySelectorAll('input')) {
            if (control.name === focused.name && control.value === focused.value) {
              control.focus({ preventScroll: true });
              break;
            }
          }
        }

        this.fillPriceFallback(url, priceMax);
        this.syncBrands(url);
        this.updateCount();
      }

      get brandGroup() {
        return this.querySelector('details[data-facets-brands]');
      }

      /**
       * Tick the brands `url` applies. Shopify reports an applied tag only when
       * it is one of the 100 its facet returns, so the server leaves most
       * applied brands unticked — and the next Apply would then drop them.
       * Values may arrive repeated or comma-joined; Shopify accepts both.
       *
       * `openApplied` opens the group when a brand is ticked, as the server
       * does for any applied group. Only on connect: on a refresh the group
       * keeps whatever the shopper left it as.
       *
       * @param {URL} url
       * @param {boolean} [openApplied]
       */
      syncBrands(url, openApplied = false) {
        const group = this.brandGroup;
        if (!group) return;

        const applied = url.searchParams.getAll(BRAND_PARAM).flatMap((value) => value.split(','));
        let anyTicked = false;

        /* The brands in a menu link's path (/collections/akron/new_era) are
           applied by the path, not the query, so they are ticked regardless;
           the form posts without them, so applying moves them into the query.
           Comma-wrapped by the Liquid — `,new_era,`. */
        const pathBrands = (group.dataset.pathBrands ?? '').split(',').filter(Boolean);
        const inputs = Array.from(group.querySelectorAll('input[type="checkbox"]'));
        for (const input of inputs) {
          input.checked = applied.includes(input.value) || pathBrands.includes(input.value);
          if (input.checked) anyTicked = true;
        }

        if (anyTicked && openApplied) group.open = true;

        this.brandBase = brandBase(url, group.dataset.basePath);
        this.updateCount();

        /* Counted already (this session, or this page) — hide straight away.
           Otherwise the counting waits for the drawer to open, so a shopper
           who never filters costs no requests. */
        const tags = inputs.map((input) => input.value);
        if (this.dialog?.open || brandCounts.has(this.brandBase.href) || storedBrandCounts(this.brandBase, tags)) {
          this.showBrandCounts();
        }
      }

      /**
       * Hide the brands the result set has no products for. A ticked brand
       * stays, so the shopper can untick what emptied the grid; a brand whose
       * count failed stays, since showing one too many beats hiding a real
       * one. The group goes when nothing in it is left.
       */
      async showBrandCounts() {
        const group = this.brandGroup;
        if (!group || !this.brandBase) return;

        const inputs = Array.from(group.querySelectorAll('input[type="checkbox"]'));
        const base = this.brandBase;
        const counts = await loadBrandCounts(
          base,
          inputs.map((input) => input.value),
        );

        /* The drawer was replaced, or moved to another result set, while
           counting; its successor applies the counts itself. */
        if (!this.isConnected || this.brandBase !== base || this.brandGroup !== group) return;

        for (const input of inputs) {
          const row = input.closest('.facets-drawer__value');
          if (row) row.hidden = counts[input.value] === 0 && !input.checked;
        }
        group.hidden = inputs.every((input) => input.closest('.facets-drawer__value')?.hidden);
      }

      fillPriceFallback(url, priceMax) {
        const fallback = this.querySelector('[data-price-fallback]');
        if (!fallback) return;

        for (const input of fallback.querySelectorAll('input')) {
          input.value = url.searchParams.get(input.name) ?? '';
          if (!priceMax) continue;

          input.max = priceMax;
          if (input.name.endsWith('.lte')) input.placeholder = priceMax;
        }

        this.updateCount();
      }

      /**
       * Counts what is currently selected in the form.
       *
       * A price range counts as one filter however many of its two ends are
       * filled, which is how Shopify counts it server-side — so the badge does
       * not jump from 1 to 2 when a shopper types the second number.
       *
       * A disabled box is the implied category (component-facets-value.liquid):
       * ticked for the shopper, not by them, and not in the URL, so it is not
       * a filter the badge should claim.
       */
      updateCount() {
        if (!this.form || !this.badge) return;

        const checked = this.form.querySelectorAll('input[type="checkbox"]:checked:not(:disabled)').length;
        const priceInputs = Array.from(this.form.querySelectorAll('input[type="number"]'));
        const hasPrice = priceInputs.some((input) => input.value.trim() !== '');

        const total = checked + (hasPrice ? 1 : 0);
        this.badge.textContent = String(total);
        this.dataset.activeCount = String(total);
      }
    },
  );
}
